import { useState } from 'react';
import { ComboBox, Option } from '@/components/ui/combo-box';
import { useUiPickerObjects, PickerObject } from '@/hooks/ui/use-ui-picker-objects';
import { Button } from '@/components/button';

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-1 w-[13.125rem]">
      <div className="h-5 animate-pulse rounded-md bg-gray-200"></div>
      <div className="h-[2.375rem] animate-pulse rounded-md bg-gray-200"></div>
    </div>
  );
}

export function ObjectPickerForm({
  type,
  label,
  options: optionsProp,
  filter,
  onCreateNew,
  createNewLoading,
  onSubmit,
}: {
  type: string;
  label?: string;
  options?: Option[];
  filter?: (item: PickerObject) => boolean;
  onCreateNew?: () => void;
  createNewLoading?: boolean;
  onSubmit: (option: Option | null) => void;
}) {
  const { data: pickerObjects } = useUiPickerObjects(optionsProp ? '' : type);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  const pickerOptions = optionsProp ?? Array.from(
    new Set(
      (pickerObjects?.data.items ?? [])
        .filter((item) => !filter || filter(item))
        .map((item) => JSON.stringify({ value: item.uuid, label: item.label }))
    )
  ).map((str) => JSON.parse(str));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    onSubmit(selectedOption);
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-end space-x-2">
        <div className="flex-1">
          {optionsProp || pickerObjects ? (
            <ComboBox
              label={label || type.charAt(0).toUpperCase() + type.slice(1)}
              options={pickerOptions}
              name="container"
              value={selectedOption}
              onChange={(value) => setSelectedOption(value)}
            />
          ) : (
            <LoadingSkeleton />
          )}
        </div>

        <Button type="submit">Select</Button>
      </div>
      {onCreateNew && (
        <button
          type="button"
          onClick={onCreateNew}
          disabled={createNewLoading}
          className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createNewLoading ? (
            <>
              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Generating…
            </>
          ) : (
            <>Create new ↗</>
          )}
        </button>
      )}
    </form>
  );
}
